import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { AuthProvider } from "@/contexts/auth";
import { CountryProvider } from "@/contexts/country";
// Keep NotificationProvider import as it might be used by other components
import { NotificationProvider } from "@/contexts/notifications/NotificationProvider";
import { CookieConsentBanner } from "@/components/cookie/CookieConsentBanner";
import { useThemeInitialization } from "@/hooks/use-theme";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import StorageInitializer from "@/components/storage/StorageInitializer";
import RealtimeInitializer from "@/components/social/RealtimeInitializer";
import { hasLaunchDatePassed } from "@/utils/date-constants";
import CommentsFeedback from "./pages/CommentsFeedback";
import InsightsToCardsRedirect from "@/components/routing/InsightsToCardsRedirect";
import ScrollToTop from "@/components/routing/ScrollToTop";

// Import pages
import Index from "@/pages/Index";
import Auth from "@/pages/Auth";
import CivicEducation from "@/pages/CivicEducation";
import Countries from "@/pages/Countries";
import Polls from "@/pages/Polls";
import PollPage from "@/pages/PollPage";
import CountryCards from "@/pages/CountryCards";
import InsightPage from "@/pages/InsightPage";
import InsightsPage from "@/pages/InsightsPage"; // Add the new Insights page
import Profile from "@/pages/Profile";
import VerificationProcess from "@/pages/VerificationProcess";
import PollsterDashboard from "@/pages/PollsterDashboard";
import DashboardPolls from "@/pages/DashboardPolls";
import NotFound from "@/pages/NotFound";
import CaymanLanding from "@/pages/CaymanLanding";
import CaymanEnhanced from "@/pages/CaymanEnhanced";
import CaymanEnhanced2 from "@/pages/CaymanEnhanced2"; // Add the new page import
import TestCommentsPage from "./pages/TestComments";

// Import legal pages
import PrivacyPolicy from "@/pages/legal/PrivacyPolicy";
import TermsOfService from "@/pages/legal/TermsOfService";
import GDPRCompliance from "@/pages/legal/GDPRCompliance";
import CodeOfConduct from "@/pages/legal/CodeOfConduct";

// Import components
import CountryRedirectWrapper from "@/components/routing/CountryRedirectWrapper";

// Import admin routes
import { adminRoutes } from "@/routes/admin-routes";

// Updated Root route handler to always show the main index page with geolocation
const RootRouteHandler = () => {
  const location = useLocation();

  // Always render the main Index page with country redirection
  // but without forcing users to the waitlist
  return (
    <CountryRedirectWrapper shouldRedirect={false}>
      <Index />
    </CountryRedirectWrapper>
  );
};

const queryClient = new QueryClient();

document.title = "NationCard™ — The Pulse of a Nation";

function App() {
  useThemeInitialization();

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Router>
          <ScrollToTop />
          <AuthProvider>
            <CountryProvider>
              <NotificationProvider>
                <StorageInitializer />
                <RealtimeInitializer />
                <CookieConsentBanner />
                <Routes>
                  {/* Root route now always uses the main homepage */}
                  <Route path="/" element={<RootRouteHandler />} />

                  <Route path="/auth" element={<Auth />} />
                  <Route path="/civic-education" element={<CivicEducation />} />
                  <Route path="/countries" element={<Countries />} />
                  <Route path="/polls" element={<Polls />} />

                  {/* Updated routes for polls with both ID and poll_number based access */}
                  <Route path="/poll/:pollId" element={<PollPage />} />
                  <Route path="/polls/:pollNumber" element={<PollPage />} />

                  <Route
                    path="/cards/:countryCode"
                    element={<CountryCards />}
                  />

                  <Route
                    path="/insights/:countryCode"
                    element={<InsightsToCardsRedirect />}
                  />

                  {/* Add new Insights page route */}
                  <Route path="/insights" element={<InsightsPage />} />

                  <Route path="/insight/:slug" element={<InsightPage />} />

                  {/* Cayman Islands landing pages - keep them accessible but not as default */}
                  <Route path="/cayman" element={<CaymanLanding />} />
                  <Route path="/cayman-enhanced" element={<CaymanEnhanced />} />
                  <Route
                    path="/cayman-enhanced2"
                    element={<CaymanEnhanced2 />}
                  />

                  {/* User profile and verification routes */}
                  <Route
                    path="/profile"
                    element={
                      <ProtectedRoute>
                        <Profile />
                      </ProtectedRoute>
                    }
                  />

                  {/* 
                    The information page is public, but steps are protected via component-level checks.
                    This allows users to view verification info without logging in but requires auth for the actual process.
                  */}
                  <Route
                    path="/verification-process"
                    element={<VerificationProcess />}
                  />

                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute requiredRole="pollster">
                        <PollsterDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/dashboard/polls"
                    element={
                      <ProtectedRoute requiredRole="pollster">
                        <DashboardPolls />
                      </ProtectedRoute>
                    }
                  />

                  {/* Legal pages */}
                  <Route
                    path="/legal/privacy-policy"
                    element={<PrivacyPolicy />}
                  />
                  <Route
                    path="/legal/terms-of-service"
                    element={<TermsOfService />}
                  />
                  <Route
                    path="/legal/gdpr-compliance"
                    element={<GDPRCompliance />}
                  />
                  <Route
                    path="/legal/code-of-conduct"
                    element={<CodeOfConduct />}
                  />

                  {/* Add test comments page for testing */}
                  <Route path="/test-comments" element={<TestCommentsPage />} />

                  {/* Add the comment feedback page */}
                  <Route
                    path="/comments-feedback"
                    element={<CommentsFeedback />}
                  />

                  {adminRoutes}

                  <Route
                    path="/admin"
                    element={<Navigate to="/admin/dashboard" replace />}
                  />

                  <Route path="*" element={<NotFound />} />
                </Routes>
              </NotificationProvider>
            </CountryProvider>
          </AuthProvider>
        </Router>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
